import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, InputAdornment, Menu,
  MenuItem, Paper, Stack, Switch, TextField, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { SoftDataGrid } from "../../../components/data/SoftDataGrid";
import { PermissionGate } from "../../../components/auth/PermissionGate";
import {
  createWifiPackage, deleteWifiPackage, getWifiPackage,
  getWifiPackages, updateWifiPackage,
} from "../../../services/wifiPackagesService";
import { getApiErrorMessage } from "../../../utils/apiData";

const fieldSx = {
  "& .MuiInputBase-root": { minHeight: 38, borderRadius: 0, fontSize: "0.72rem" },
  "& .MuiInputLabel-root": { fontSize: "0.72rem" },
  "& .MuiFormHelperText-root": { mx: 0, fontSize: "0.63rem" },
};

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};
const round = (value, precision = 2) => {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};
const extractPaginator = (payload) => payload?.packages ?? payload?.data?.packages ?? payload?.data ?? payload ?? {};
const extractRows = (payload) => {
  const paginator = extractPaginator(payload);
  if (Array.isArray(paginator?.data)) return paginator.data;
  if (Array.isArray(paginator)) return paginator;
  return [];
};
const initials = (name) => String(name || "WP").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

function formatMinutes(total) {
  const minutes = numberValue(total);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  return [days && `${days}d`, hours && `${hours}h`, (mins || (!days && !hours)) && `${mins}m`].filter(Boolean).join(" ");
}

function formatSeconds(total) {
  const seconds = numberValue(total);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return [hours && `${hours}h`, minutes && `${minutes}m`, (secs || (!hours && !minutes)) && `${secs}s`].filter(Boolean).join(" ");
}

function SquareDialog({ open, onClose, title, subtitle, children, actions, maxWidth = "md" }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
      <DialogTitle sx={{ borderBottom: "1px solid #E3E7EC" }}>
        <Typography sx={{ fontSize: "0.92rem", fontWeight: 850 }}>{title}</Typography>
        {subtitle ? <Typography sx={{ mt: 0.25, color: "#667085", fontSize: "0.67rem" }}>{subtitle}</Typography> : null}
      </DialogTitle>
      <DialogContent sx={{ p: 2.2, bgcolor: "#FBFCFD" }}>{children}</DialogContent>
      {actions ? <DialogActions sx={{ borderTop: "1px solid #E3E7EC" }}>{actions}</DialogActions> : null}
    </Dialog>
  );
}

function WifiPackageForm({ wifiPackage, busy, onCancel, onSubmit }) {
  const validity = wifiPackage?.validity_minutes || 60;
  const timeout = wifiPackage?.session_timeout_seconds || 3600;
  const { control, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: wifiPackage?.name || "",
      validity_hours: round(validity / 60),
      validity_minutes: validity,
      timeout_hours: round(timeout / 3600),
      timeout_minutes: round(timeout / 60),
      session_timeout_seconds: timeout,
      is_active: wifiPackage?.is_active ?? true,
    },
  });

  const validityHours = watch("validity_hours");
  const validityMinutes = watch("validity_minutes");
  const timeoutHours = watch("timeout_hours");
  const timeoutMinutes = watch("timeout_minutes");
  const timeoutSeconds = watch("session_timeout_seconds");

  const setValidityHours = (value) => {
    const hours = numberValue(value);
    setValue("validity_hours", value, { shouldValidate: true });
    setValue("validity_minutes", Math.round(hours * 60), { shouldValidate: true });
  };
  const setValidityMinutes = (value) => {
    const minutes = numberValue(value);
    setValue("validity_minutes", value, { shouldValidate: true });
    setValue("validity_hours", round(minutes / 60), { shouldValidate: true });
  };
  const setTimeoutHours = (value) => {
    const seconds = Math.round(numberValue(value) * 3600);
    setValue("timeout_hours", value, { shouldValidate: true });
    setValue("timeout_minutes", round(seconds / 60), { shouldValidate: true });
    setValue("session_timeout_seconds", seconds, { shouldValidate: true });
  };
  const setTimeoutMinutes = (value) => {
    const seconds = Math.round(numberValue(value) * 60);
    setValue("timeout_minutes", value, { shouldValidate: true });
    setValue("timeout_hours", round(seconds / 3600), { shouldValidate: true });
    setValue("session_timeout_seconds", seconds, { shouldValidate: true });
  };
  const setTimeoutSeconds = (value) => {
    const seconds = numberValue(value);
    setValue("session_timeout_seconds", value, { shouldValidate: true });
    setValue("timeout_minutes", round(seconds / 60), { shouldValidate: true });
    setValue("timeout_hours", round(seconds / 3600), { shouldValidate: true });
  };

  const submit = (values) => onSubmit({
    name: values.name.trim(),
    validity_minutes: Math.max(1, Math.round(numberValue(values.validity_minutes))),
    session_timeout_seconds: Math.max(1, Math.round(numberValue(values.session_timeout_seconds))),
    is_active: Boolean(values.is_active),
  });

  return (
    <Box component="form" onSubmit={handleSubmit(submit)}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "220px 1fr" }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 0, textAlign: "center" }}>
          <Avatar sx={{ width: 92, height: 92, mx: "auto", bgcolor: "#EEF3FF", color: "#2F5BEA", fontWeight: 850 }}>
            {initials(wifiPackage?.name || "Wi-Fi Package")}
          </Avatar>
          <Typography sx={{ mt: 1.4, fontSize: "0.78rem", fontWeight: 800 }}>{wifiPackage ? "Package settings" : "New package"}</Typography>
          <Typography sx={{ mt: 0.35, color: "#7A8696", fontSize: "0.64rem" }}>Enter hours, minutes or seconds. Related fields update automatically.</Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 0 }}>
          <Controller name="name" control={control} rules={{ required: "Package name is required." }} render={({ field }) => (
            <TextField {...field} label="Package name" fullWidth error={Boolean(errors.name)} helperText={errors.name?.message} sx={fieldSx} />
          )} />

          <Typography sx={{ mt: 2, mb: 1, fontSize: "0.68rem", fontWeight: 850 }}>PACKAGE VALIDITY</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.2 }}>
            <Controller name="validity_hours" control={control} render={({ field }) => (
              <TextField {...field} type="number" label="Validity hours" inputProps={{ min: 0, step: 0.25 }} onChange={(e) => setValidityHours(e.target.value)} sx={fieldSx} />
            )} />
            <Controller name="validity_minutes" control={control} rules={{ min: { value: 1, message: "At least 1 minute." } }} render={({ field }) => (
              <TextField {...field} type="number" label="Validity minutes" inputProps={{ min: 1, step: 1 }} onChange={(e) => setValidityMinutes(e.target.value)} error={Boolean(errors.validity_minutes)} helperText={errors.validity_minutes?.message} sx={fieldSx} />
            )} />
          </Box>
          <Paper variant="outlined" sx={{ mt: 1, p: 1, borderRadius: 0, bgcolor: "#F8FAFC" }}>
            <Typography sx={{ fontSize: "0.66rem" }}>Stored: <strong>{numberValue(validityMinutes)}</strong> minutes = <strong>{round(numberValue(validityHours))}</strong> hours</Typography>
            <Typography sx={{ fontSize: "0.66rem", fontWeight: 750 }}>{formatMinutes(validityMinutes)}</Typography>
          </Paper>

          <Typography sx={{ mt: 2, mb: 1, fontSize: "0.68rem", fontWeight: 850 }}>SESSION TIMEOUT</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 1.2 }}>
            <Controller name="timeout_hours" control={control} render={({ field }) => (
              <TextField {...field} type="number" label="Timeout hours" inputProps={{ min: 0, step: 0.25 }} onChange={(e) => setTimeoutHours(e.target.value)} sx={fieldSx} />
            )} />
            <Controller name="timeout_minutes" control={control} render={({ field }) => (
              <TextField {...field} type="number" label="Timeout minutes" inputProps={{ min: 0, step: 1 }} onChange={(e) => setTimeoutMinutes(e.target.value)} sx={fieldSx} />
            )} />
            <Controller name="session_timeout_seconds" control={control} rules={{ min: { value: 1, message: "At least 1 second." } }} render={({ field }) => (
              <TextField {...field} type="number" label="Timeout seconds" inputProps={{ min: 1, step: 1 }} onChange={(e) => setTimeoutSeconds(e.target.value)} error={Boolean(errors.session_timeout_seconds)} helperText={errors.session_timeout_seconds?.message} sx={fieldSx} />
            )} />
          </Box>
          <Paper variant="outlined" sx={{ mt: 1, p: 1, borderRadius: 0, bgcolor: "#F8FAFC" }}>
            <Typography sx={{ fontSize: "0.66rem" }}>Stored: <strong>{numberValue(timeoutSeconds)}</strong> seconds = <strong>{round(numberValue(timeoutMinutes))}</strong> minutes = <strong>{round(numberValue(timeoutHours))}</strong> hours</Typography>
            <Typography sx={{ fontSize: "0.66rem", fontWeight: 750 }}>{formatSeconds(timeoutSeconds)}</Typography>
          </Paper>

          <Controller name="is_active" control={control} render={({ field }) => (
            <FormControlLabel sx={{ mt: 1.2 }} control={<Switch size="small" checked={Boolean(field.value)} onChange={(_, checked) => field.onChange(checked)} />} label={<Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>Active package</Typography>} />
          )} />
        </Paper>
      </Box>
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
        <Button color="inherit" variant="outlined" onClick={onCancel} disabled={busy} sx={{ borderRadius: 0 }}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={busy} sx={{ borderRadius: 0 }}>{busy ? "Saving..." : wifiPackage ? "Save changes" : "Create package"}</Button>
      </Stack>
    </Box>
  );
}

function RowMenu({ row, onView, onEdit, onDelete }) {
  const [anchor, setAnchor] = useState(null);
  const choose = (handler) => { setAnchor(null); handler(row); };
  return (
    <>
      <IconButton size="small" onClick={(event) => setAnchor(event.currentTarget)}><MoreVertRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} PaperProps={{ sx: { borderRadius: 0, minWidth: 180 } }}>
        <MenuItem onClick={() => choose(onView)} sx={{ gap: 1, fontSize: "0.7rem" }}><VisibilityOutlinedIcon sx={{ fontSize: 16 }} />View details</MenuItem>
        <PermissionGate permission="update_wifi_packages"><MenuItem onClick={() => choose(onEdit)} sx={{ gap: 1, fontSize: "0.7rem" }}><EditOutlinedIcon sx={{ fontSize: 16 }} />Edit package</MenuItem></PermissionGate>
        <PermissionGate permission="delete_wifi_packages"><MenuItem onClick={() => choose(onDelete)} sx={{ gap: 1, color: "error.main", fontSize: "0.7rem" }}><DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />Delete package</MenuItem></PermissionGate>
      </Menu>
    </>
  );
}

export function WifiPackagesPage() {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [dialog, setDialog] = useState({ type: null, wifiPackage: null });
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPaginationModel((current) => ({ ...current, page: 0 }));
    }, 450);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const packagesQuery = useQuery({
    queryKey: ["wifi-packages", search, paginationModel.page, paginationModel.pageSize],
    queryFn: () => getWifiPackages({ search: search || undefined, page: paginationModel.page + 1, per_page: paginationModel.pageSize }),
    placeholderData: (previous) => previous,
  });

  const paginator = extractPaginator(packagesQuery.data);
  const rows = extractRows(packagesQuery.data);

  const detailsQuery = useQuery({
    queryKey: ["wifi-package", dialog.wifiPackage?.id],
    queryFn: () => getWifiPackage(dialog.wifiPackage.id),
    enabled: dialog.type === "view" && Boolean(dialog.wifiPackage?.id),
  });
  const detailPackage = detailsQuery.data?.package ?? detailsQuery.data?.data?.package ?? dialog.wifiPackage;

  const saveMutation = useMutation({
    mutationFn: ({ wifiPackage, values }) => wifiPackage ? updateWifiPackage(wifiPackage.id, values) : createWifiPackage(values),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ["wifi-packages"] });
      setDialog({ type: null, wifiPackage: null });
      setMessage({ title: variables.wifiPackage ? "Package updated" : "Package created", text: response?.message || "Wi-Fi package saved successfully." });
    },
    onError: (error) => setMessage({ title: "Unable to save package", text: getApiErrorMessage(error) }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteWifiPackage,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["wifi-packages"] });
      setDialog({ type: null, wifiPackage: null });
      setMessage({ title: "Package deleted", text: response?.message || "Wi-Fi package deleted successfully." });
    },
    onError: (error) => setMessage({ title: "Unable to delete package", text: getApiErrorMessage(error) }),
  });

  const columns = useMemo(() => [
    { field: "avatar", headerName: "", width: 54, sortable: false, renderCell: (params) => <Avatar sx={{ width: 28, height: 28, bgcolor: "#EEF3FF", color: "#2F5BEA", fontSize: "0.6rem", fontWeight: 850 }}>{initials(params.row.name)}</Avatar> },
    { field: "name", headerName: "Package name", minWidth: 220, flex: 1, renderCell: (params) => <Typography sx={{ fontSize: "0.72rem", fontWeight: 800 }}>{params.row.name}</Typography> },
    { field: "validity_minutes", headerName: "Validity minutes", minWidth: 140 },
    { field: "validity_display", headerName: "Validity", minWidth: 150, valueGetter: (_, row) => formatMinutes(row.validity_minutes) },
    { field: "session_timeout_seconds", headerName: "Timeout seconds", minWidth: 145 },
    { field: "timeout_display", headerName: "Session timeout", minWidth: 160, valueGetter: (_, row) => formatSeconds(row.session_timeout_seconds) },
    { field: "status", headerName: "Status", width: 90, renderCell: (params) => <Chip label={(params.row.is_active ?? true) ? "Active" : "Inactive"} size="small" sx={{ height: 22, borderRadius: 0, bgcolor: (params.row.is_active ?? true) ? "#E7F6EE" : "#F1F3F5", color: (params.row.is_active ?? true) ? "#18794E" : "#667085", fontSize: "0.65rem", fontWeight: 750 }} /> },
    { field: "created_at", headerName: "Created", minWidth: 130, valueFormatter: (value) => value ? new Date(value).toLocaleDateString("en-ZA") : "—" },
    { field: "actions", headerName: "", width: 54, sortable: false, renderCell: (params) => <RowMenu row={params.row} onView={(wifiPackage) => setDialog({ type: "view", wifiPackage })} onEdit={(wifiPackage) => setDialog({ type: "edit", wifiPackage })} onDelete={(wifiPackage) => setDialog({ type: "delete", wifiPackage })} /> },
  ], []);

  return (
    <Box sx={{ p: { xs: 1.2, md: 2 } }}>
      <Paper variant="outlined" sx={{ borderRadius: 0 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ p: 1.2, borderBottom: "1px solid #E3E7EC" }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 850 }}>Wi-Fi packages</Typography>
            <Typography sx={{ mt: 0.25, color: "#667085", fontSize: "0.66rem" }}>Manage package validity and session timeout values.</Typography>
          </Box>
          <TextField size="small" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search packages" sx={{ width: { xs: "100%", md: 280 }, "& .MuiInputBase-root": { height: 36, borderRadius: 0, fontSize: "0.7rem" } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 16 }} /></InputAdornment> }} />
          <PermissionGate permission="create_wifi_packages"><Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDialog({ type: "create", wifiPackage: null })} sx={{ borderRadius: 0 }}>New package</Button></PermissionGate>
        </Stack>
        <SoftDataGrid rows={rows} columns={columns} loading={packagesQuery.isLoading} rowCount={paginator?.total ?? rows.length} paginationMode="server" paginationModel={paginationModel} onPaginationModelChange={setPaginationModel} pageSizeOptions={[10, 20, 50, 100]} getRowId={(row) => row.id} />
      </Paper>

      <SquareDialog open={dialog.type === "create" || dialog.type === "edit"} onClose={() => setDialog({ type: null, wifiPackage: null })} title={dialog.type === "edit" ? "Edit Wi-Fi package" : "Create Wi-Fi package"} subtitle="Hours, minutes and seconds are converted automatically before saving.">
        <WifiPackageForm key={`${dialog.type}-${dialog.wifiPackage?.id || "new"}`} wifiPackage={dialog.type === "edit" ? dialog.wifiPackage : null} busy={saveMutation.isPending} onCancel={() => setDialog({ type: null, wifiPackage: null })} onSubmit={(values) => saveMutation.mutate({ wifiPackage: dialog.type === "edit" ? dialog.wifiPackage : null, values })} />
      </SquareDialog>

      <SquareDialog open={dialog.type === "view"} onClose={() => setDialog({ type: null, wifiPackage: null })} title="Wi-Fi package details" actions={<Button variant="contained" onClick={() => setDialog({ type: null, wifiPackage: null })} sx={{ borderRadius: 0 }}>Close</Button>}>
        {detailPackage ? <Stack spacing={1}>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 850 }}>{detailPackage.name}</Typography>
          <Typography sx={{ fontSize: "0.7rem" }}>Validity: {detailPackage.validity_minutes} minutes ({round(detailPackage.validity_minutes / 60)} hours)</Typography>
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 750 }}>{formatMinutes(detailPackage.validity_minutes)}</Typography>
          <Typography sx={{ fontSize: "0.7rem" }}>Timeout: {detailPackage.session_timeout_seconds} seconds ({round(detailPackage.session_timeout_seconds / 60)} minutes / {round(detailPackage.session_timeout_seconds / 3600)} hours)</Typography>
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 750 }}>{formatSeconds(detailPackage.session_timeout_seconds)}</Typography>
        </Stack> : null}
      </SquareDialog>

      <SquareDialog open={dialog.type === "delete"} onClose={() => setDialog({ type: null, wifiPackage: null })} title="Delete Wi-Fi package" subtitle="This action cannot be undone." maxWidth="sm" actions={<><Button color="inherit" onClick={() => setDialog({ type: null, wifiPackage: null })} sx={{ borderRadius: 0 }}>Cancel</Button><Button color="error" variant="contained" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate(dialog.wifiPackage.id)} sx={{ borderRadius: 0 }}>{deleteMutation.isPending ? "Deleting..." : "Delete package"}</Button></>}>
        <Typography sx={{ fontSize: "0.72rem" }}>Delete <strong>{dialog.wifiPackage?.name}</strong>?</Typography>
      </SquareDialog>

      <Dialog open={Boolean(message)} onClose={() => setMessage(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontSize: "0.82rem", fontWeight: 850 }}>{message?.title}</DialogTitle>
        <DialogContent dividers><Typography sx={{ fontSize: "0.7rem" }}>{message?.text}</Typography></DialogContent>
        <DialogActions><Button variant="contained" onClick={() => setMessage(null)} sx={{ borderRadius: 0 }}>OK</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
